import { LoadingButton } from '@mui/lab';
import { Box, Button, CardContent, LinearProgress } from '@mui/material';
import { useConfig } from '@revenuewell/logic-config';
import { useNotification } from '@revenuewell/logic-notification';
import { useOidc } from '@revenuewell/logic-oidc';
import { ImageLibraryService } from 'apps/wrapper/src/services/practice-portal/image-library/image-library-service';
import { CropAndSaveRequest } from 'apps/wrapper/src/services/practice-portal/image-library/types/crop-save-request';
import { ImageCategoryType } from 'apps/wrapper/src/services/practice-portal/image-library/types/image-category-type';
import { FC, useEffect, useMemo, useState } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import { Card } from '../../card/card.style';
import {
  CloseDialogButton,
  Dialog,
  DialogContent,
  DialogContentWrapper,
  DialogTitle
} from '../../dialog/dialog.styles';
import { Hr, ImageContainer, ToolbarSection } from './crop-image.styles';

export interface CropImageDialogProps {
  open: boolean;
  imageUrl: string;
  locationId: number;
  onClose: () => void;
  onCrop: (filename: string) => void;
}

export const CropImageDialog: FC<CropImageDialogProps> = (props) => {
  const { open, imageUrl, locationId, onClose, onCrop } = props;
  const { config } = useConfig();
  const { oidcService } = useOidc();
  const { showError } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [zoom, setZoom] = useState(1.5);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [cropArea, setCropArea] = useState<Area | null>(null);
  const imageLibraryService = useMemo(() => new ImageLibraryService(config, oidcService), [config, oidcService]);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      // there is no image load event in react-easy-crop library,
      // so simulate loading with a timeout
      setTimeout(() => setIsLoading(false), 3500);
    }
  }, [open]);

  const handleCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCropArea(croppedAreaPixels);
  };

  const handleSubmitClick = async () => {
    if (!cropArea) return;

    const request = {
      fileName: imageUrl.split('Email_')[1],
      imageCategoryType: ImageCategoryType.CustomCampaign,
      x1: cropArea.x,
      y1: cropArea.y,
      x2: cropArea.x + cropArea.width,
      y2: cropArea.y + cropArea.height
    } as CropAndSaveRequest;

    try {
      setIsSaving(true);
      const response = await imageLibraryService.cropAndSaveImage(locationId, request);
      if (!response.Data.IsSuccess) throw new Error('Unable to crop image.');
      onCrop(response.Data.Message);
    } catch (error) {
      console.error(error);
      showError('Unable to crop image.');
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} maxWidth={false}>
      <DialogContentWrapper>
        <DialogTitle sx={{ cursor: 'default' }}>
          Image Library - Crop Image
          <CloseDialogButton onClick={onClose} />
        </DialogTitle>
        <DialogContent sx={{ width: '620px', padding: '40px 20px 0 20px' }}>
          <Card>
            <CardContent style={{ padding: 0 }}>
              <ImageContainer>
                {isLoading && (
                  <Box sx={{ margin: 'auto' }}>
                    <LinearProgress sx={{ width: '120px', height: '6px', marginBottom: '6px' }} />
                    <Box sx={{ textAlign: 'center' }}>Loading image...</Box>
                  </Box>
                )}
                {!isLoading && (
                  <Cropper
                    image={imageUrl}
                    crop={crop}
                    zoom={zoom}
                    aspect={4 / 3}
                    onCropChange={setCrop}
                    onCropComplete={handleCropComplete}
                    onZoomChange={setZoom}
                  />
                )}
              </ImageContainer>
              <Hr />
              <ToolbarSection>
                <Box sx={{ display: 'flex', padding: '0 16px 0 0', marginLeft: 'auto' }}>
                  <Button variant="text" onClick={() => onClose && onClose()} color="error">
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={isSaving}
                    variant="contained"
                    onClick={handleSubmitClick}
                    disabled={isLoading}
                    disableElevation
                  >
                    Crop & Use
                  </LoadingButton>
                </Box>
              </ToolbarSection>
            </CardContent>
          </Card>
        </DialogContent>
      </DialogContentWrapper>
    </Dialog>
  );
};
